'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getClient } from '../lib/browser';
import { trackAction } from '../lib/tracking';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

interface PWAContextType {
  canInstall: boolean;
  install: () => Promise<void>;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

/**
 * PWA Provider Component
 * Manages the beforeinstallprompt event at the app level
 */
export function PWAProvider({ children }: { children: ReactNode }) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  
  const client = getClient();
  const isInstalled = client !== 'web'; // Already installed if not in web browser

  // Initialize state from global prompt and listen for changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    console.log('🎯 PWA Provider: Initializing with global prompt');
    console.log('🔍 PWA Provider: globalDeferredPrompt:', globalDeferredPrompt);
    
    // Initialize from global prompt if available
    if (globalDeferredPrompt) {
      setDeferredPrompt(globalDeferredPrompt);
      console.log('✅ PWA Provider: Initialized with existing global prompt');
      
      // Track that PWA prompt became available
      trackAction('PWA Prompt Available', {
        client_type: client,
        platform: navigator.platform || 'unknown'
      });
    }

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('✅ PWA Provider: App was installed');
      setDeferredPrompt(null); // Clear prompt after installation
      globalDeferredPrompt = null; // Clear global prompt too
      trackAction('PWA Install Completed', {
        client_type: client,
        platform: navigator.platform || 'unknown'
      });
    };

    // Listen for prompt availability updates
    const handlePromptAvailable = () => {
      console.log('📢 PWA Provider: Received prompt available notification');
      if (globalDeferredPrompt && !deferredPrompt) {
        console.log('🔄 PWA Provider: Syncing with global prompt');
        setDeferredPrompt(globalDeferredPrompt);
        
        // Track that PWA prompt became available
        trackAction('PWA Prompt Available', {
          client_type: client,
          platform: navigator.platform || 'unknown'
        });
      }
    };

    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('pwa-prompt-available', handlePromptAvailable);
    
    return () => {
      console.log('🧹 PWA Provider: Cleaning up PWA listeners');
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('pwa-prompt-available', handlePromptAvailable);
    };
      }, [client, deferredPrompt]);

  const install = useCallback(async () => {
    console.log('🚀 PWA Provider: Install button clicked!');
    console.log('🔍 PWA Provider: globalDeferredPrompt:', globalDeferredPrompt);
    console.log('🔍 PWA Provider: state deferredPrompt:', deferredPrompt);
    
    // Use global prompt as authoritative source
    const promptToUse = globalDeferredPrompt || deferredPrompt;
    
    // Track install attempt
    trackAction('PWA Install Attempted', {
      client_type: client,
      has_prompt: promptToUse ? 'yes' : 'no'
    });
    
    if (!promptToUse) {
      console.warn('❌ PWA Provider: No install prompt available');
      trackAction('PWA Install Failed', {
        client_type: client,
        reason: 'no_prompt_available'
      });
      return;
    }

    try {
      console.log('📱 PWA Provider: Calling prompt()...');
      await promptToUse.prompt();
      
      console.log('⏳ PWA Provider: Waiting for user choice...');
      const result = await promptToUse.userChoice;

      console.log(`PWA Provider: User choice: ${result.outcome}`);
      
      // Track user choice
      trackAction('PWA Install User Choice', {
        client_type: client,
        outcome: result.outcome,
        platform: result.platform || 'unknown'
      });
      
      // Clear both prompts after use (can only be used once)
      setDeferredPrompt(null);
      globalDeferredPrompt = null;
    } catch (error) {
      console.error('💥 PWA Provider: Installation failed:', error);
      trackAction('PWA Install Failed', {
        client_type: client,
        reason: 'prompt_error',
        error: error instanceof Error ? error.message : 'unknown'
      });
    }
  }, [deferredPrompt, client]);

  // Simple canInstall calculation: not installed + have working prompt
  const prompt = globalDeferredPrompt || deferredPrompt;
  const canInstall = !isInstalled && !!(prompt && typeof prompt.prompt === 'function');

  console.log('🎯 PWA Provider: canInstall =', canInstall, {
    isInstalled,
    client,
    hasPrompt: !!prompt
  });

  const value: PWAContextType = {
    canInstall,
    install
  };

  return (
    <PWAContext.Provider value={value}>
      {children}
    </PWAContext.Provider>
  );
}

/**
 * Hook to use PWA context
 * Must be used within PWAProvider
 */
export function usePWA(): PWAContextType {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
}

// Store the deferred prompt at module level to persist across React re-renders
// The beforeinstallprompt event only fires once per page load
let globalDeferredPrompt: BeforeInstallPromptEvent | null = null;

// Early event capture for cases where provider mounts after event fires
if (typeof window !== 'undefined') {
  console.log('🎯 Module: Setting up beforeinstallprompt listener');
  
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    console.log('🔥 Module: beforeinstallprompt event captured early!', e);
    e.preventDefault();
    // Store globally for all provider instances to use
    globalDeferredPrompt = e as BeforeInstallPromptEvent;
    
    console.log('✅ Module: globalDeferredPrompt stored, dispatching custom event');
    
    // Notify all provider instances that prompt became available
    window.dispatchEvent(new CustomEvent('pwa-prompt-available'));
  });
  
  // Log initial state
  console.log('🔍 Module: Initial PWA state:', {
    hasManifest: !!document.querySelector('link[rel="manifest"]'),
    isHttps: window.location.protocol === 'https:',
    hasServiceWorker: 'serviceWorker' in navigator,
    userAgent: navigator.userAgent.substring(0, 100) + '...'
  });
} 